/**
 * Zentrale Preiskonfiguration für Charlies Crew
 * 
 * WICHTIG: Bei Preisänderungen müssen auch die Edge Functions aktualisiert werden:
 * - supabase/functions/stripe-webhook/index.ts (ADVENTURE_PASS_CONFIG)
 * - supabase/functions/create-pass-checkout/index.ts (PASS_PRICES Kommentare)
 * - supabase/functions/chat/index.ts (Chatbot-Prompt)
 * 
 * Stripe-Price-IDs stehen nur in supabase/functions/_shared/pass-catalog.ts (Checkout,
 * Webhook und Zahlungs-Wächter lesen von dort). Die Legacy-Liste STRIPE_PRICE_IDS hier
 * war ungenutzt und wurde am 27.10.2026 entfernt (Q-D1).
 */

export type PassType = 'single' | 'three' | 'five' | 'ten' | 'five_regular' | 'ten_regular';

// --- Tier-aware pricing ---
export type PriceTier = 'legacy' | '2026_07' | '2026_08';

/** Display mirror of supabase/functions/_shared/pass-catalog.ts. Server remains authoritative. */
export const PASS_PRICES_BY_TIER: Record<PriceTier, Record<PassType, number>> = {
  legacy:   { single: 44, three: 79, five: 175, ten: 333, five_regular: 205, ten_regular: 390 },
  '2026_07': { single: 49, three: 79, five: 175, ten: 333, five_regular: 230, ten_regular: 440 },
  // Einstiegskarte 139 € mit 60 € Anrechnung auf die nächste 5er/10er. Sonst wie 2026_07.
  '2026_08': { single: 49, three: 139, five: 175, ten: 333, five_regular: 230, ten_regular: 440 },
};

/** Normalise a raw price_tier string from the DB. Mirrors _shared/pass-catalog.ts. */
export function normalizeTier(raw: string | null | undefined): PriceTier {
  if (raw === 'legacy') return 'legacy';
  if (raw === '2026_08') return '2026_08';
  return '2026_07';
}

/** Return the display price for a pass type in the given tier. */
export function priceFor(passType: PassType, tier: PriceTier): number {
  return PASS_PRICES_BY_TIER[tier][passType];
}
// --------------------------

export interface PassConfig {
  id: PassType;
  name: string;
  price: number;
  credits: number;
  pricePerCredit: number;
  savings?: string;
  badge?: string;
  footnote?: string;
  limitedQuantity?: number;
  isOneTime?: boolean;
  hidden?: boolean;
}

// Zentrale Preiskonfiguration — Schaufenster-Preise für Neukunden / nicht eingeloggte Besucher (2026_07-Tarif).
// Eingeloggte Legacy-Kunden sehen ihre Tier-Preise über priceFor()/normalizeTier().
export const PASS_PRICES: Record<PassType, number> = {
  single: 49,           // Tageskarte (2026_07)
  three: 139,           // 3er Karte (Einstiegskarte, 60 € Anrechnung auf die nächste Karte)
  five: 175,            // 5er Karte (35€/Tag) - Willkommenspreis
  ten: 333,             // 10er Karte (33,30€/Tag) - Willkommenspreis
  five_regular: 230,    // 5er Karte regulär (46€/Tag, 2026_07)
  ten_regular: 440,     // 10er Karte regulär (44€/Tag, 2026_07)
};

// Vollständige Pass-Produkte für Shop-Anzeige
export const PASS_PRODUCTS: PassConfig[] = [
  {
    id: 'single',
    name: 'Tageskarte',
    price: PASS_PRICES.single,
    credits: 1,
    pricePerCredit: PASS_PRICES.single,
  },
  {
    id: 'three',
    name: 'Einstiegs 3er Karte',
    price: PASS_PRICES.three,
    credits: 3,
    pricePerCredit: Math.round((PASS_PRICES.three / 3) * 100) / 100,
    footnote: '60 € Gutschrift für deine nächste Karte',
    isOneTime: true,
    badge: 'Einmalig',
  },
  {
    id: 'five',
    name: 'Willkommens 5er Karte',
    price: PASS_PRICES.five,
    credits: 5,
    pricePerCredit: PASS_PRICES.five / 5,
    savings: 'Spare 45€',
    footnote: '* Preise für die ersten Kunden - danach 41,00€',
    limitedQuantity: 35,
    hidden: true,
  },
  {
    id: 'ten',
    name: 'Willkommens 10er Karte',
    price: PASS_PRICES.ten,
    credits: 10,
    pricePerCredit: PASS_PRICES.ten / 10,
    savings: 'Spare 107€',
    badge: 'Beliebt',
    footnote: '* Preise für die ersten Kunden - danach 39,00€',
    limitedQuantity: 33,
    hidden: true,
  },
  {
    id: 'five_regular',
    name: '5er Karte',
    price: PASS_PRICES.five_regular,
    credits: 5,
    pricePerCredit: PASS_PRICES.five_regular / 5,
    savings: 'Spare 15€',
  },
  {
    id: 'ten_regular',
    name: '10er Karte',
    price: PASS_PRICES.ten_regular,
    credits: 10,
    pricePerCredit: PASS_PRICES.ten_regular / 10,
    savings: 'Spare 50€',
    badge: 'Beliebt',
  },
];

// Pass-Optionen für Admin-Bereich
export const PASS_OPTIONS = PASS_PRODUCTS.map(p => ({
  value: p.id,
  label: `${p.name} (${p.credits} ${p.credits === 1 ? 'Tag' : 'Tage'})`,
  defaultPrice: p.price,
}));

// Helper für Pass-Type Labels
export const getPassTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    single: 'Tageskarte',
    three: 'Einstiegs 3er Karte',
    five: '5er Karte',
    ten: '10er Karte',
    five_regular: '5er Karte',
    ten_regular: '10er Karte',
  };
  return labels[type] || type;
};

// Helper für Admin-Tabellen
export const getPassTypeLongLabel = (type: string): string => {
  const labels: Record<string, string> = {
    single: 'Tageskarte',
    three: 'Einstiegs 3er Karte',
    five: '5er Karte (Willkommenspreis)',
    ten: '10er Karte (Willkommenspreis)',
    five_regular: '5er Karte',
    ten_regular: '10er Karte',
  };
  return labels[type] || type;
};

// === Daycare-Pässe (Preise TBD — Platzhalter, vor Go-Live bestätigen) ===
export type DaycarePassType = 'daycare_single' | 'daycare_five' | 'daycare_ten';

export const DAYCARE_PASS_PRICES: Record<DaycarePassType, number> = {
  daycare_single: 0, // TODO: finalen Preis setzen
  daycare_five: 0,   // TODO
  daycare_ten: 0,    // TODO
};

// Stripe Price IDs für Daycare (im Stripe Dashboard anlegen, dann hier eintragen)
export const DAYCARE_STRIPE_PRICE_IDS: Record<DaycarePassType, string> = {
  daycare_single: 'PLACEHOLDER_daycare_single',
  daycare_five: 'PLACEHOLDER_daycare_five',
  daycare_ten: 'PLACEHOLDER_daycare_ten',
};

export const DAYCARE_PASS_CREDITS: Record<DaycarePassType, number> = {
  daycare_single: 1,
  daycare_five: 5,
  daycare_ten: 10,
};
